import { AuthStorage } from "utils/auth-storage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Container from "components/container";
import Text from "components/text";
import { ReportServices } from "apis/Services/ReportService";
import LoadingComponent from "components/loading-component";
import ExportReports from "components/export-reports";
import ExportReportsCSV from "components/export-reports-csv";
import ReactECharts from "echarts-for-react";
import { Button, Dropdown, Empty, Select, Table } from "antd";
import { DownOutlined } from "@ant-design/icons";
import styles from "./SubmittalSubmissionScheduleReport.module.css";
import {
  canAccessCannedReport,
  normalizeReportAccessLevel,
  parseCannedReportsGranularPermission,
} from "../canned-reports/reportPermissions";

const CHART_COLORS = [
  "#5BAA83",
  "#B8C98B",
  "#F5C84C",
  "#EB955D",
  "#5F8EE4",
  "#FFD66B",
  "#AA7AE8",
  "#5DC0C9",
  "#F28DA8",
  "#8FB4FF",
];

const DIVISION_LABELS = {
  "00": "Procurement & Contracting Requirements",
  "01": "General Requirements",
  "02": "Existing Conditions",
  "03": "Concrete",
  "04": "Masonry",
  "05": "Metals",
  "06": "Wood, Plastics & Composites",
  "07": "Thermal & Moisture",
  "08": "Openings",
  "09": "Finishes",
  10: "Specialties",
  11: "Equipment",
  12: "Furnishings",
  13: "Special Construction",
  14: "Conveying Equipment",
  21: "Fire Suppression",
  22: "Plumbing",
  23: "HVAC",
  25: "Integrated Automation",
  26: "Electrical",
  27: "Communications",
  28: "Electronic Safety & Security",
  31: "Earthwork",
  32: "Exterior Improvements",
  33: "Utilities",
  34: "Transportation",
  35: "Waterway & Marine",
};

function firstFilledValue(...values) {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === "string" && value.trim() === "") {
      continue;
    }

    return value;
  }

  return "";
}

function normalizeTextValue(value) {
  return String(firstFilledValue(value, "")).trim();
}

function parseMmDdYyyy(value) {
  const normalizedValue = normalizeTextValue(value);
  const match = normalizedValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);

  if (!match) {
    return null;
  }

  const [, month, day, year] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

function compareTextValues(leftValue, rightValue) {
  return normalizeTextValue(leftValue).localeCompare(
    normalizeTextValue(rightValue),
    undefined,
    {
      numeric: true,
      sensitivity: "base",
    },
  );
}

function compareDateValues(leftValue, rightValue) {
  const leftTimestamp = parseMmDdYyyy(leftValue);
  const rightTimestamp = parseMmDdYyyy(rightValue);

  if (leftTimestamp === null && rightTimestamp === null) {
    return 0;
  }

  if (leftTimestamp === null) {
    return 1;
  }

  if (rightTimestamp === null) {
    return -1;
  }

  return leftTimestamp - rightTimestamp;
}

function looksLikePeriodBucket(item) {
  return Boolean(
    item &&
    typeof item === "object" &&
    !Array.isArray(item) &&
    Object.prototype.hasOwnProperty.call(item, "periodStart") &&
    Object.prototype.hasOwnProperty.call(item, "periodEnd") &&
    Object.prototype.hasOwnProperty.call(item, "divisions"),
  );
}

function formatDivisionLabel(divisionKey, divisionValue) {
  const normalizedKey = normalizeTextValue(divisionKey);

  if (DIVISION_LABELS[normalizedKey]) {
    return `${normalizedKey} ${DIVISION_LABELS[normalizedKey]}`;
  }

  const subdivisionKeys = Object.keys(divisionValue?.subdivisions || {});

  if (subdivisionKeys.length === 1) {
    const onlySubdivision = subdivisionKeys[0];
    const trimmedSubdivision = onlySubdivision.includes(" - ")
      ? onlySubdivision.split(" - ").slice(1).join(" - ")
      : onlySubdivision;

    return `${normalizedKey} ${trimmedSubdivision}`.trim();
  }

  return normalizedKey || "Unassigned";
}

function normalizeSubmittalsScheduledPeriods(response) {
  const candidates = [
    response?.content,
    response?.periods,
    response?.data,
    response?.content?.periods,
  ];

  const rawPeriods =
    candidates.find(
      (candidate) =>
        Array.isArray(candidate) &&
        (candidate.length === 0 || candidate.every(looksLikePeriodBucket)),
    ) || [];

  return rawPeriods.map((period, periodIndex) => {
    const divisionEntries = Object.entries(period?.divisions || {})
      .map(([divisionKey, divisionValue], divisionIndex) => {
        const subdivisions = Object.entries(
          divisionValue?.subdivisions || {},
        ).map(([subdivisionKey, subdivisionValue], subdivisionIndex) => ({
          key: subdivisionKey,
          label: normalizeTextValue(subdivisionKey),
          totalCount:
            Number(
              firstFilledValue(
                subdivisionValue?.totalCount,
                subdivisionValue?.submittals?.length,
                0,
              ),
            ) || 0,
          submittals: Array.isArray(subdivisionValue?.submittals)
            ? subdivisionValue.submittals
            : [],
          sortOrder: subdivisionIndex,
        }));

        return {
          key: normalizeTextValue(divisionKey),
          label: formatDivisionLabel(divisionKey, divisionValue),
          totalCount:
            Number(
              firstFilledValue(
                divisionValue?.totalCount,
                subdivisions.reduce(
                  (accumulator, subdivision) =>
                    accumulator + Number(subdivision.totalCount || 0),
                  0,
                ),
                0,
              ),
            ) || 0,
          subdivisions,
          sortOrder: divisionIndex,
        };
      })
      .sort((left, right) =>
        left.key.localeCompare(right.key, undefined, { numeric: true }),
      );

    const divisionCounts = divisionEntries.reduce((accumulator, division) => {
      accumulator[division.key] = division.totalCount;
      return accumulator;
    }, {});

    return {
      key: `${normalizeTextValue(period?.periodStart)}-${normalizeTextValue(
        period?.periodEnd,
      )}-${periodIndex}`,
      label:
        normalizeTextValue(period?.label) ||
        normalizeTextValue(period?.periodStart),
      periodStart: normalizeTextValue(period?.periodStart),
      periodEnd: normalizeTextValue(period?.periodEnd),
      totalCount:
        Number(
          firstFilledValue(
            period?.totalCount,
            divisionEntries.reduce(
              (accumulator, division) =>
                accumulator + Number(division.totalCount || 0),
              0,
            ),
            0,
          ),
        ) || 0,
      divisions: divisionEntries,
      divisionCounts,
    };
  });
}

function flattenScheduledRows(periods) {
  const flattenedRows = [];

  periods.forEach((period) => {
    period.divisions.forEach((division) => {
      division.subdivisions.forEach((subdivision) => {
        subdivision.submittals.forEach((submittal, submittalIndex) => {
          flattenedRows.push({
            ...submittal,
            __rowKey: firstFilledValue(
              submittal?.entryID,
              submittal?.id,
              `${period.key}-${division.key}-${subdivision.key}-${submittalIndex}`,
            ),
            __periodKey: period.key,
            __periodLabel: period.label,
            __periodStart: period.periodStart,
            __periodEnd: period.periodEnd,
            __divisionKey: division.key,
            __divisionLabel: division.label,
            __subdivisionKey: subdivision.key,
            __subdivisionLabel: subdivision.label,
          });
        });
      });
    });
  });

  return flattenedRows;
}

function extractFilterOptions(response, flattenedRows) {
  const contractorOptions =
    [
      response?.dropDownCompanyNames,
      response?.content?.dropDownCompanyNames,
      response?.filterOptions?.responsibleContractor,
      response?.content?.filterOptions?.responsibleContractor,
    ].find((value) => Array.isArray(value) && value.length > 0) || [];

  const specTypeOptions =
    [
      response?.dropDownSpecTypes,
      response?.content?.dropDownSpecTypes,
      response?.filterOptions?.specType,
      response?.content?.filterOptions?.specType,
    ].find((value) => Array.isArray(value) && value.length > 0) || [];

  const fallbackContractors = Array.from(
    new Set(
      flattenedRows
        .map((row) => normalizeTextValue(row?.responsibleContractor))
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right));

  const fallbackSpecTypes = Array.from(
    new Set(
      flattenedRows
        .map((row) => normalizeTextValue(row?.specType))
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right));

  return {
    contractors: (contractorOptions.length
      ? contractorOptions
      : fallbackContractors
    )
      .map((value) => normalizeTextValue(value))
      .filter(Boolean),
    specTypes: (specTypeOptions.length ? specTypeOptions : fallbackSpecTypes)
      .map((value) => normalizeTextValue(value))
      .filter(Boolean),
  };
}

function buildChartModel(periods) {
  const groupMap = new Map();

  periods.forEach((period) => {
    period.divisions.forEach((division) => {
      if (!groupMap.has(division.key)) {
        groupMap.set(division.key, {
          key: division.key,
          label: division.label,
          color: CHART_COLORS[groupMap.size % CHART_COLORS.length],
        });
      }
    });
  });

  const groups = Array.from(groupMap.values()).sort((left, right) =>
    left.key.localeCompare(right.key, undefined, { numeric: true }),
  );

  return {
    periods,
    groups,
  };
}

function buildChartOption(periods, groups) {
  return {
    color: groups.map((group) => group.color),
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: "#ffffff",
      borderColor: "#d8e3f0",
      borderWidth: 1,
      textStyle: {
        color: "#1f2937",
      },
      formatter: (params) => {
        if (!params?.length) {
          return "";
        }

        const period = periods[params[0].dataIndex];
        const rows = [
          `<div style="font-weight:600;margin-bottom:6px;">${
            period?.label || ""
          }</div>`,
        ];

        params
          .filter((item) => Number(item?.value) > 0)
          .forEach((item) => {
            rows.push(
              `<div style="margin:3px 0;color:${item.color};">${item.seriesName}: ${item.value}</div>`,
            );
          });

        rows.push(
          `<div style="margin-top:8px;font-weight:600;color:#111827;">Total: ${
            period?.totalCount || 0
          }</div>`,
        );

        return rows.join("");
      },
    },
    legend: {
      type: "scroll",
      selectedMode: false,
      bottom: 0,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: {
        fontSize: 12,
        color: "#516173",
      },
    },
    grid: {
      left: 36,
      right: 24,
      top: 44,
      bottom: 76,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: periods.map((period) => period.label),
      axisTick: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: "#dce6f2",
        },
      },
      axisLabel: {
        color: "#62758c",
      },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      splitLine: {
        lineStyle: {
          type: "dashed",
          color: "#e7eef6",
        },
      },
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: "#62758c",
      },
    },
    series: groups.map((group) => ({
      id: group.key,
      name: group.label,
      type: "bar",
      stack: "submittals",
      emphasis: {
        focus: "series",
      },
      barWidth: "100%",
      barMaxWidth: 100,
      data: periods.map((period) => period.divisionCounts[group.key] || 0),
      itemStyle: {
        borderRadius: 0,
      },
    })),
  };
}

function buildSubmissionStatus(row) {
  const submittedDate = normalizeTextValue(row?.submittalIssued);

  if (submittedDate) {
    return {
      text: "Submitted",
      color: "processing",
    };
  }

  const responseValue = normalizeTextValue(row?.specResponse).toLowerCase();

  if (responseValue === "rejected") {
    return {
      text: "Rejected",
      color: "error",
    };
  }

  if (responseValue === "approved") {
    return {
      text: "Approved",
      color: "success",
    };
  }

  return {
    text: "Pending",
    color: "warning",
  };
}

function buildTableRows(rows, selectedSegment) {
  const filteredRows = selectedSegment
    ? rows.filter(
        (row) =>
          row.__periodKey === selectedSegment.periodKey &&
          row.__divisionKey === selectedSegment.divisionKey,
      )
    : rows;

  return filteredRows.map((row) => {
    const status = buildSubmissionStatus(row);

    return {
      key: row.__rowKey,
      submittalNumber: normalizeTextValue(row?.specNumber),
      itemName: normalizeTextValue(row?.specTitle),
      division: row.__divisionLabel,
      subdivision: row.__subdivisionLabel,
      submittalType: normalizeTextValue(row?.specType),
      responsibleContractor: normalizeTextValue(row?.responsibleContractor),
      status: status.text,
      statusColor: status.color,
      expectedSubmission: normalizeTextValue(row?.submittalRequired),
      dateSubmitted: normalizeTextValue(row?.submittalIssued),
      periodLabel: row.__periodLabel,
      periodStart: row.__periodStart,
    };
  });
}

const SubmittalSubmissionScheduleReport = ({ company_id, id, reportName }) => {
  const chartRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccessLoading, setIsAccessLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState(null);
  const [pageAccessLevel, setPageAccessLevel] = useState(0);
  const [cannedReportsGranularPermission, setCannedReportsGranularPermission] =
    useState("");
  const [projectName, setProjectName] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [periodsData, setPeriodsData] = useState([]);
  const [groupBy, setgroupBy] = useState("monthly");
  const [filterCombination, setFilterCombination] = useState("or");
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [selectedSpecTypes, setSelectedSpecTypes] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    contractors: [],
    specTypes: [],
  });
  const [selectedSegment, setSelectedSegment] = useState(null);
  const allowedReports = parseCannedReportsGranularPermission(
    cannedReportsGranularPermission,
  );
  const canViewReport = canAccessCannedReport({
    accessLevel: pageAccessLevel,
    allowedReports,
    reportSlug: reportName,
  });

  useEffect(() => {
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    setIsAccessLoading(true);
    ReportServices.getReportAccessLevel({
      company: company_id,
      project: id,
      code,
      loginEmail,
    })
      .then((res) => {
        setPageAccessLevel(normalizeReportAccessLevel(res?.accessLevel));
        setCannedReportsGranularPermission(
          res?.cannedReportsGranularPermission || "",
        );
        setProjectName(
          (prevProjectName) => prevProjectName || res?.projectName || "",
        );
      })
      .catch((error) => {
        console.log(error);
        setPageAccessLevel(0);
        setCannedReportsGranularPermission("");
      })
      .finally(() => {
        setIsAccessLoading(false);
      });
  }, [company_id, id]);

  const fetchReport = useCallback(() => {
    setIsLoading(true);

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    const filteredColumns = {};

    if (selectedContractors.length > 0) {
      filteredColumns.responsibleContractor = selectedContractors;
    }

    if (selectedSpecTypes.length > 0) {
      filteredColumns.specType = selectedSpecTypes;
    }

    ReportServices.getSubmittalsScheduledReport({
      company: company_id,
      project: id,
      code,
      loginEmail,
      groupBy,
      filterCombination,
      filteredColumns,
    })
      .then((res) => {
        const normalizedPeriods = normalizeSubmittalsScheduledPeriods(res);
        const flattenedRows = flattenScheduledRows(normalizedPeriods);

        setPeriodsData(normalizedPeriods);
        setAccessLevel(Number(res?.accessLevel ?? 0));
        setProjectName(normalizeTextValue(res?.projectName));
        setProjectAddress(normalizeTextValue(res?.projectAddress));
        setFilterOptions(extractFilterOptions(res, flattenedRows));
      })
      .catch((error) => {
        console.log(error);
        setPeriodsData([]);
        setFilterOptions({ contractors: [], specTypes: [] });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    company_id,
    filterCombination,
    id,
    groupBy,
    selectedContractors,
    selectedSpecTypes,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const flattenedRows = useMemo(
    () => flattenScheduledRows(periodsData),
    [periodsData],
  );

  const chartModel = useMemo(() => buildChartModel(periodsData), [periodsData]);

  useEffect(() => {
    if (!selectedSegment) {
      return;
    }

    const segmentStillExists = flattenedRows.some(
      (row) =>
        row.__periodKey === selectedSegment.periodKey &&
        row.__divisionKey === selectedSegment.divisionKey,
    );

    if (!segmentStillExists) {
      setSelectedSegment(null);
    }
  }, [flattenedRows, selectedSegment]);

  const chartOption = useMemo(
    () => buildChartOption(chartModel.periods, chartModel.groups),
    [chartModel.groups, chartModel.periods],
  );

  const chartTitle = useMemo(
    () => (groupBy === "monthly" ? "Submittals by Month" : "Submittals by Week"),
    [groupBy],
  );

  const tableColumns = useMemo(
    () => [
      {
        title: "Submittal Number",
        dataIndex: "submittalNumber",
        key: "submittalNumber",
        align: "center",
        width: 140,
        sorter: (left, right) =>
          compareTextValues(left.submittalNumber, right.submittalNumber),
      },
      {
        title: "Item Name",
        dataIndex: "itemName",
        key: "itemName",
        align: "left",
        width: 280,
        sorter: (left, right) => compareTextValues(left.itemName, right.itemName),
      },
      {
        title: "Division",
        dataIndex: "division",
        key: "division",
        align: "center",
        width: 200,
        sorter: (left, right) => compareTextValues(left.division, right.division),
      },
      {
        title: "Subdivision",
        dataIndex: "subdivision",
        key: "subdivision",
        align: "center",
        width: 260,
        sorter: (left, right) =>
          compareTextValues(left.subdivision, right.subdivision),
      },
      {
        title: "Submittal Type",
        dataIndex: "submittalType",
        key: "submittalType",
        align: "center",
        width: 180,
        sorter: (left, right) =>
          compareTextValues(left.submittalType, right.submittalType),
      },
      {
        title: "Responsible Contractor",
        dataIndex: "responsibleContractor",
        key: "responsibleContractor",
        align: "center",
        width: 220,
        sorter: (left, right) =>
          compareTextValues(
            left.responsibleContractor,
            right.responsibleContractor,
          ),
      },
      // {
      //   title: "Status",
      //   dataIndex: "status",
      //   key: "status",
      //   align: "center",
      //   width: 140,
      //   render: (_, record) => (
      //     <Tag color={record.statusColor}>{record.status}</Tag>
      //   ),
      // },
      {
        title: "Submittal Required",
        dataIndex: "expectedSubmission",
        key: "expectedSubmission",
        align: "center",
        width: 160,
        sorter: (left, right) =>
          compareDateValues(left.expectedSubmission, right.expectedSubmission),
      },
      {
        title: "Date Submitted",
        dataIndex: "dateSubmitted",
        key: "dateSubmitted",
        align: "center",
        width: 150,
        sorter: (left, right) =>
          compareDateValues(left.dateSubmitted, right.dateSubmitted),
      },
      {
        title: "Period",
        dataIndex: "periodLabel",
        key: "periodLabel",
        align: "center",
        width: 160,
        sorter: (left, right) =>
          compareDateValues(left.periodStart, right.periodStart) ||
          compareTextValues(left.periodLabel, right.periodLabel),
      },
    ],
    [],
  );

  const tableRows = useMemo(
    () => buildTableRows(flattenedRows, selectedSegment),
    [flattenedRows, selectedSegment],
  );

  const contractorSelectOptions = useMemo(
    () =>
      filterOptions.contractors.map((value) => ({
        value,
        label: value,
      })),
    [filterOptions.contractors],
  );

  const specTypeSelectOptions = useMemo(
    () =>
      filterOptions.specTypes.map((value) => ({
        value,
        label: value,
      })),
    [filterOptions.specTypes],
  );

  const handleChartClick = useCallback(
    (params) => {
      if (!params?.seriesId || params?.dataIndex === undefined) {
        return;
      }

      const nextPeriod = chartModel.periods[params.dataIndex];
      const nextGroup = chartModel.groups.find(
        (group) => group.key === params.seriesId,
      );

      if (!nextPeriod || !nextGroup) {
        return;
      }

      setSelectedSegment((previousValue) => {
        if (
          previousValue?.periodKey === nextPeriod.key &&
          previousValue?.divisionKey === nextGroup.key
        ) {
          return null;
        }

        return {
          periodKey: nextPeriod.key,
          divisionKey: nextGroup.key,
          divisionLabel: nextGroup.label,
        };
      });
    },
    [chartModel.groups, chartModel.periods],
  );

  const tableTitle = useMemo(() => {
    if (!selectedSegment) {
      return "Filter Submittal Results";
    }

    const periodLabel =
      chartModel.periods.find(
        (period) => period.key === selectedSegment.periodKey,
      )?.label || selectedSegment.periodKey;

    return `Filtered submittals for ${selectedSegment.divisionLabel} in ${periodLabel}`;
  }, [chartModel.periods, selectedSegment]);

  const getExportVisuals = useCallback(() => {
    const echartsInstance = chartRef.current?.getEchartsInstance?.();

    if (!echartsInstance || chartModel.periods.length < 1 || chartModel.groups.length < 1) {
      return [];
    }

    return [
      {
        title: chartTitle,
        subtitle: tableTitle,
        imageData: echartsInstance.getDataURL({
          type: "png",
          pixelRatio: 2,
          backgroundColor: "#ffffff",
        }),
        width: 282,
        height: 86,
        format: "PNG",
      },
    ];
  }, [chartModel.groups.length, chartModel.periods.length, chartTitle, tableTitle]);

  const exportMenuItems = useMemo(
    () => [
      {
        key: "export-pdf",
        label: (
          <ExportReports
            data={tableRows}
            columns={tableColumns}
            accessLevel={accessLevel}
            editedData={[]}
            isLoading={isLoading}
            reportTitle="Submittal Submission Schedule"
            projectName={projectName}
            projAddress={projectAddress}
            company_id={company_id}
            getExportVisuals={getExportVisuals}
          />
        ),
      },
      {
        key: "export-csv",
        label: (
          <ExportReportsCSV
            data={tableRows}
            columns={tableColumns}
            accessLevel={accessLevel}
            editedData={[]}
            isLoading={isLoading}
            reportTitle="Submittal Submission Schedule"
            projectName={projectName}
            projAddress={projectAddress}
          />
        ),
      },
    ],
    [
      accessLevel,
      company_id,
      getExportVisuals,
      isLoading,
      projectAddress,
      projectName,
      tableColumns,
      tableRows,
    ],
  );

  return (
    <Container>
      {canViewReport && (
        <>
          <div className="row mt-4">
            <div className="col-md-6">
              <Text className={styles.header}>
                Submittal Submission Schedule
              </Text>
              {/* <p className={styles.description}>
                A comprehensive report depicting when submittals are expected to
                be submitted to the design team
              </p> */}
            </div>
            <div className="col-md-6 d-flex justify-content-sm-center justify-content-md-end gap-3">
              <div className="align-self-center d-flex gap-2">
                {/* <SidebarDistribution
                  isLoading={isLoading}
                  reportName={`canned report - ${reportName}`}
                  reportOwner="Koncurent"
                /> */}

                <Dropdown menu={{ items: exportMenuItems }}>
                  <Button>
                    Export <DownOutlined />
                  </Button>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className={styles.filterBar}>
            <div className={styles.filterField}>
              <label>View</label>
              <Select
                value={groupBy}
                options={[
                  { value: "monthly", label: "Monthly" },
                  { value: "weekly", label: "Weekly" },
                ]}
                onChange={setgroupBy}
              />
            </div>
            <div className={styles.filterField}>
              <label>Filter Logic</label>
              <Select
                value={filterCombination}
                options={[
                  { value: "or", label: "OR" },
                  { value: "and", label: "AND" },
                ]}
                onChange={setFilterCombination}
              />
            </div>
            <div className={styles.filterFieldWide}>
              <label>Responsible Contractor</label>
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                value={selectedContractors}
                options={contractorSelectOptions}
                placeholder="All contractors"
                onChange={setSelectedContractors}
              />
            </div>
            <div className={styles.filterFieldWide}>
              <label>Submittal Type</label>
              <Select
                mode="multiple"
                allowClear
                maxTagCount="responsive"
                value={selectedSpecTypes}
                options={specTypeSelectOptions}
                placeholder="All submittal types"
                onChange={setSelectedSpecTypes}
              />
            </div>
          </div>
        </>
      )}

      <div className="row mt-4">
        {isLoading || isAccessLoading ? (
          <LoadingComponent />
        ) : !canViewReport ? (
          <div className="row">
            <div className="text-center">
              <Text>
                You do not have the necessary permissions to access this page.
              </Text>
            </div>
          </div>
        ) : (
          <div className={styles.reportLayout}>
            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <div>
                  <Text className={styles.chartTitle}>{chartTitle}</Text>
                  <p className={styles.chartHint}>
                    Hover a period to see the division counts, then click a bar
                    segment to update the table underneath.
                  </p>
                </div>
                {selectedSegment && (
                  <Button size="small" onClick={() => setSelectedSegment(null)}>
                    Clear Focus
                  </Button>
                )}
              </div>

              {chartModel.periods.length > 0 && chartModel.groups.length > 0 ? (
                <ReactECharts
                  ref={chartRef}
                  option={chartOption}
                  style={{ height: 440, width: "100%" }}
                  onEvents={{ click: handleChartClick }}
                />
              ) : (
                <div className={styles.chartEmptyState}>
                  <Empty description="No scheduled submittals found for the selected filters." />
                </div>
              )}
            </div>

            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <Text className={styles.tableTitle}>{tableTitle}</Text>
                <span className={styles.tableCount}>
                  {tableRows.length} Submittal
                  {tableRows.length === 1 ? "" : "s"}
                </span>
              </div>

              <Table
                columns={tableColumns}
                dataSource={tableRows}
                bordered
                size="small"
                scroll={{ x: 1500, y: "62vh" }}
                pagination={{
                  position: ["bottomCenter"],
                  pageSize: 100,
                  showSizeChanger: false,
                }}
                locale={{
                  emptyText: "No submittals found for the current selection.",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default SubmittalSubmissionScheduleReport;
